import type { FC } from "react";
import { Col, Row } from "@viagogo/core/ui";
import { AddFeature, RecordParticipationTypes } from "@viagogo/feature";
import type { CategoryEventListViewModel } from "src/http/api-types";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { EmailSignUp } from "src/website-apps/shared/components/email-signup";
import { EventListRecentlyViewedGrid } from "src/website-apps/shared/components/event-list-recently-viewed-grid";
import { FiltersAndGridsManager } from "src/website-apps/shared/components/FiltersAndGridsManager";
import { FeaturedShowEventsModule } from "src/website-apps/shared/components/theater-promo-banner/FeaturedShowEventsModule";
import { UserEventToastOnAncillaryPages } from "src/website-apps/shared/components/user-event-toast";
import { canShowWorldCupFanProtectDisplay } from "src/website-apps/shared/components/world-cup-fan-protect/eligibility";
import { eventListPlacement } from "src/website-apps/shared/components/world-cup-fan-protect/styles";
import { WorldCupFanProtectDisplay } from "src/website-apps/shared/components/world-cup-fan-protect/WorldCupFanProtectDisplay";
import type { FeaturedShowModuleConfig } from "../TheaterPromoBannerFeature";
import { CategoryTopPerformersCarousel } from "../CategoryTopPerformersCarousel";
import { MaybeVenuesLinksGrid } from "./MaybeVenuesLinksGrid";
import {
  BodyWrapper,
  MarginDivLarge,
  PaddedContainer,
  PromoFilterSpacing,
  VerticalMarginRow,
} from "./styled-components";

interface Props {
  model: CategoryEventListViewModel;
  featuredCategoryIds?: readonly number[] | undefined;
  featuredShow?: FeaturedShowModuleConfig | undefined;
  // True while the promo hero is rendered above, so the body card rounds and lifts over it.
  overlapsHero?: boolean | undefined;
}

/**
 * New Version of the LayoutBody Component for Control to Add New Tests in ONLY for Applicable Categories
 */
export const LayoutBody: FC<Props> = ({ model, featuredCategoryIds, featuredShow, overlapsHero }) => {
  const appContext = useAppContext();
  const { categoryId, categoryName } = model.categorySummary ?? {};
  const canShowWorldCupFanProtect = canShowWorldCupFanProtectDisplay(categoryId, appContext.cobrandId);
  const featuredShowEvents = model.featuredShowEvents ?? [];

  return (
    <>
      <UserEventToastOnAncillaryPages userEventOption={model.userEventOption} />
      <BodyWrapper addPrimaryBgColor $secondaryBgColor $overlapsHero={overlapsHero}>
        <PaddedContainer $overlapsHero={overlapsHero}>
          <Row between={[true]}>
            <Col span={[12]}>
              {featuredShow && featuredShowEvents.length > 0 ? (
                <MarginDivLarge $flushStart={overlapsHero} $tightEnd={overlapsHero}>
                  <FeaturedShowEventsModule
                    show={{
                      name: featuredShow.name,
                      moduleKicker: featuredShow.moduleKicker,
                      profileImageUrl: model.featuredShowImageUrl ?? undefined,
                      url: featuredShow.url,
                    }}
                    events={featuredShowEvents}
                    categoryId={model.categoryId}
                    entityId={featuredShow.entityId}
                    carouselId={featuredShow.carouselId}
                  />
                </MarginDivLarge>
              ) : null}
              <AddFeature
                id={FeatureSetEnum.TMWorldCupFanProtectDisplay}
                recordType={RecordParticipationTypes.IN_VIEW}
                canParticipate={() => canShowWorldCupFanProtect}
                feature={() => <WorldCupFanProtectDisplay mode="responsive" className={eventListPlacement} />}
              />
              {/* Promo variants only: the control keeps Top Performers inside the immersive header
                  (passed there as `carouselSlot`), so it must not also render in the body. */}
              {overlapsHero && model.popularCategories ? (
                <MarginDivLarge $flushStart={overlapsHero} $flushEnd={overlapsHero}>
                  <CategoryTopPerformersCarousel
                    categoryId={model.categoryId}
                    categoryName={categoryName ?? undefined}
                    popularCategories={model.popularCategories}
                    featuredCategoryIds={featuredCategoryIds}
                    promoExperiment={overlapsHero}
                  />
                </MarginDivLarge>
              ) : null}
              <PromoFilterSpacing $promo={overlapsHero}>
                <FiltersAndGridsManager filterTypeOptions={model.gridFilterTypes} categoryName={categoryName ?? ""} />
              </PromoFilterSpacing>
              <MarginDivLarge>
                <MaybeVenuesLinksGrid
                  isGrouping={model.isGrouping}
                  categoryName={categoryName ?? ""}
                  venues={model.venues ?? []}
                />
              </MarginDivLarge>
            </Col>
          </Row>
          <Row>
            <EventListRecentlyViewedGrid />
          </Row>
          <VerticalMarginRow>
            {!appContext.isNativeApp && (
              <Col span={[12]}>
                <EmailSignUp emailPrivacyDisclosureSiteMessage={model.privacyPolicyMessage} />
              </Col>
            )}
          </VerticalMarginRow>
        </PaddedContainer>
      </BodyWrapper>
    </>
  );
};
